import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Typography } from "@material-ui/core";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  container: {
    backgroundColor: "#FFFFFF",
    borderRadius: "8px",
    border: "1px solid #D5E0EB",
    padding: theme.spacing(3),
    marginTop: theme.spacing(3),
    boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 0,
    paddingBottom: 0,
  },
  headerLeft: {
    display: "flex",
    flexDirection: "column",
    gap: 0,
    margin: 0,
    padding: 0,
  },
  icon: {
    width: 20,
    height: 20,
    marginRight: theme.spacing(1.5),
    color: "#495057",
  },
  title: {
    fontFamily: "Inter, sans-serif",
    fontSize: "18px",
    fontWeight: 600,
    color: "#212529",
    lineHeight: "24px",
    margin: 0,
    padding: 0,
  },
  pillsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    marginTop: "24px",
    marginBottom: "24px",
    padding: 0,
  },
  pill: {
    width: "96px",
    height: "32px",
    backgroundColor: "#FFF1B8",
    border: "1px solid #FAAD14",
    borderRadius: "16px",
    borderWidth: "1px",
    padding: "8.5px",
    fontFamily: "Inter, sans-serif",
    fontSize: "12px",
    fontWeight: 500,
    fontStyle: "normal",
    color: "#FAAD14",
    lineHeight: "100%",
    letterSpacing: "0%",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap",
    boxSizing: "border-box",
  },
  tableContainer: {
    marginTop: 0,
    paddingTop: 0,
  },
  createButton: {
    backgroundColor: "#6F42C1",
    color: "#FFFFFF",
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "20px",
    padding: theme.spacing(1, 2),
    borderRadius: "6px",
    textTransform: "none",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#5A32A3",
      boxShadow: "none",
    },
    "&:disabled": {
      backgroundColor: "#E1E1E1",
      color: "#8F8F8F",
    },
    "&:disabled $buttonIcon": {
      color: "#8F8F8F",
    },
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  buttonIcon: {
    width: 16,
    height: 16,
    marginRight: theme.spacing(1),
    color: "#FFFFFF",
  },
}));

/**
 * BoxInventory Component
 * 
 * Displays box inventory information with type pills and create button
 * 
 * @param {Array} inventory - Array of inventory items with { type, current, total }
 * @param {Function} onCreateBoxOrder - Handler for create box order button
 * @param {ReactNode} children - Table component to render inside container
 * @param {string} className - Additional CSS classes
 */
const BoxInventory = ({ inventory, onCreateBoxOrder, children, className }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <div className={`${classes.container} ${className || ""}`}>
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <div style={{ display: "flex", alignItems: "center", margin: 0, padding: 0 }}>
            <svg
              className={classes.icon}
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20 6H16L14 4H10L8 6H4C2.9 6 2 6.9 2 8V19C2 20.1 2.9 21 4 21H20C21.1 21 22 20.1 22 19V8C22 6.9 21.1 6 20 6ZM12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17ZM12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12C15 10.34 13.66 9 12 9Z"
                fill="currentColor"
              />
            </svg>
            <Typography className={classes.title}>{t("Box Inventory")}</Typography>
          </div>
          <div className={classes.pillsContainer}>
            {inventory && Array.isArray(inventory) && inventory.length > 0 ? (
              inventory.map((item, index) => {
                // Display 0 if quantity is negative
                const displayQuantity = item.current < 0 ? 0 : item.current;
                return (
                  <span key={index} className={classes.pill}>
                    {item.type}: {displayQuantity}/{item.total}
                  </span>
                );
              })
            ) : null}
          </div>
        </div>
      </div>

      {children && <div className={classes.tableContainer}>{children}</div>}
    </div>
  );
};

BoxInventory.propTypes = {
  inventory: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      current: PropTypes.number.isRequired,
      total: PropTypes.number.isRequired,
    })
  ),
  onCreateBoxOrder: PropTypes.func,
  children: PropTypes.node,
  className: PropTypes.string,
};

BoxInventory.defaultProps = {
  onCreateBoxOrder: null,
  children: null,
  className: "",
};

export default BoxInventory;

